// Angular
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';

// Afidium
import { IMailTreatment } from 'libraries/service/resources/model/processing/IMailTreatment';
import { formGroupConfig, getValidators } from './config/form';
import { WORDING } from './config/wording';
import { IOptionsCardForm } from 'libraries/module/formbuilder/forms/card-form/card-form.component';
import { IFieldSpecs } from 'libraries/module/formbuilder/fields/interfaces/field-specs';
import { ProcessingEmailService } from '../../services/processing-email.service';
import { MailBreadcrumbService } from '../../services/email-breadcrumb.service';
import { IBreadcrumbItem } from 'libraries/service/breadcrumb/IBreadcrumbItem';
import { AuthService } from 'libraries/service/auth/auth.service';
import { IVariableEntry } from 'libraries/model/IVariableEntry';
import {
    EmailTextGenerationService
} from 'libraries/service/resources/email-text-generation/email-text-generation.service';
import { ILabels } from 'libraries/interfaces/ILabels';
import { TitleService } from 'app_sasha/shared/services/title/title.service';
import { Yii } from 'libraries/interfaces/Yii';
import { IOverLayout } from "app_sasha/shared/interfaces/IEmail";

@Component({
    selector: 'mail-form',
    template: `
        <div class="app-wrapper app-sasha">
            <breadcrumb [items]="breadcrumbItems"></breadcrumb>

            <card>
                <div class="card-header-content">
                    <h1>{{ wording.header }}</h1>

                    <div>
                        <back-button></back-button>
                        <btnForm styleType="actionDo" (click)="previousView()">
                            {{ wording.previousStep }}
                        </btnForm>
                        <btnForm styleType="actionPrincipal" (click)="goToValidationView()" class="e2e-btn-preview">
                            {{ wording.nextStep }}
                        </btnForm>
                    </div>
                </div>
                <div class="card-body-content card-padding-inside">
                    <loader *ngIf="emailTemplateLoader.active"
                            [message]="emailTemplateLoader.message"></loader>

                    <form *ngIf="fieldConfig && !emailTemplateLoader.active" novalidate [formGroup]="form">
                        <text [fieldConfig]="fieldConfig.replyTo"></text>
                        <chips class="sep-top" [fieldConfig]="fieldConfig.cc"></chips>
                        <chips class="sep-top" [fieldConfig]="fieldConfig.bcc"></chips>
                        <text class="sep-top" [fieldConfig]="fieldConfig.subject"></text>
                        <rich-textarea-field-with-variables
                                class="sep-top"
                                [fieldConfig]="fieldConfig.body"
                                [label]="fieldConfig.body.label"
                                [variablesConfig]="{ canUseVariables: true, help: variableConfig }">
                        </rich-textarea-field-with-variables>
                    </form>
                </div>
            </card>
        </div>
    `,
    styles: [`
        text.sep-top, chips.sep-top, rich-textarea-field-with-variables.sep-top {
            display: block;
        }
    `]
})
export class EmailFormComponent implements OnInit {
    @Input()
    public templateValues: IMailTreatment;

    @Output()
    public readonly onPreviousView: EventEmitter<null> = new EventEmitter();

    @Output()
    public readonly onPreviousRoute: EventEmitter<null> = new EventEmitter();

    @Output()
    public readonly onGoToValidationView: EventEmitter<IMailTreatment> = new EventEmitter();

    public wording: ILabels = WORDING;
    public form: FormGroup;
    public cardFormOptions: IOptionsCardForm;
    public fieldConfig: {
        cc: IFieldSpecs;
        bcc: IFieldSpecs;
        subject: IFieldSpecs;
        body: IFieldSpecs;
        replyTo: IFieldSpecs;
    };
    public breadcrumbItems: IBreadcrumbItem[] = [];
    public emailTemplateLoader: {
        active: boolean;
        message: string;
    } = {
        active: false,
        message: null
    };

    public modelName: string;
    public variableConfig: IVariableEntry;

    public from: string;

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly router: Router,
        private readonly processingEmailService: ProcessingEmailService,
        private readonly mailBreadcrumbService: MailBreadcrumbService,
        private readonly authService: AuthService,
        private readonly emailTextGenerationService: EmailTextGenerationService,
        private readonly titleService: TitleService,
    ) {
    }

    public ngOnInit(): void {
        this.breadcrumbItems = this.mailBreadcrumbService.breadcrumbForm();

        this.form = formGroupConfig(this.formBuilder);
        this.titleService.setTitle(
            Yii.t("wui-sasha-js", "Rédaction d'un email")
        );

        this.fieldConfig = {
            cc: this.getFieldConfig('cc'),
            bcc: this.getFieldConfig('bcc'),
            subject: this.getFieldConfig('subject'),
            body: this.getFieldConfig('body'),
            replyTo: this.getFieldConfig('replyTo')
        };

        if (this.processingEmailService.form) {
            this.modelName = this.processingEmailService.form.name;
            this.patchTemplateValuesInForm(this.processingEmailService.form);
        }

        this.variableConfig = this.emailTextGenerationService.getHelpModel();
    }

    public patchTemplateValuesInForm(emailTemplate): void {
        const overLayout: IOverLayout = emailTemplate.parameter ? emailTemplate.parameter.overLayout : null;
        this.from = overLayout ? overLayout.from : null;
        this.form.patchValue({
            id: emailTemplate.id,
            cc: overLayout && overLayout.cc ? overLayout.cc : emailTemplate.cc,
            bcc: overLayout && overLayout.bcc ? overLayout.bcc : emailTemplate.bcc,
            replyTo: emailTemplate.replyTo ?
                emailTemplate.replyTo :
                overLayout && overLayout.replyTo ?
                    overLayout.replyTo :
                    this.authService.userContact.email,
            subject: emailTemplate.parameter ? emailTemplate.parameter.title : emailTemplate.subject,
            body: emailTemplate.parameter ? emailTemplate.parameter.body : emailTemplate.body,
        });
    }

    public goToValidationView(): void {
        if (this.form.valid) {
            this.processingEmailService.form = {
                ...this.form.value,
                from: this.from,
                ...{modelName: this.modelName}
            };
            this.router.navigate(['/sasha/processing-email/validation'], {
    queryParamsHandling: 'preserve'
});
        } else {
            Object.entries(this.form.controls)
                .forEach(([key, control]) => {
                    control.markAsTouched();
                });
        }
    }

    public previousView(): void {
        this.router.navigate(['/sasha/processing-email'], {
    queryParamsHandling: 'preserve'
});
    }

    public previousRoute(): void {
        this.onPreviousRoute.emit();
    }

    private getFieldConfig(formControlName: string): IFieldSpecs {
        return {
            formGroup: this.form,
            label: WORDING[formControlName],
            formControlName,
            validators: getValidators(formControlName)
        };
    }
}
